import { useEffect, useState } from 'react';
import { customerPortalAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Star, MessageSquare, Plus, Trash2, Award, CheckCircle, 
  Clock, XCircle, Quote, Sparkles, Loader2
} from 'lucide-react';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  featured: 'bg-purple-100 text-purple-700'
};

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  featured: Award
};

/**
 * CustomerPortalTestimonialsPage - Testimonial submission for customers
 */
const CustomerPortalTestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  
  // Submit form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    rating: 5,
    company_name: '',
    job_title: '',
    use_case: '',
    allow_public: true,
    allow_photo: false
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const res = await customerPortalAPI.getMyTestimonials();
      setTestimonials(res.data.testimonials || []);
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Please provide a title and your testimonial');
      return;
    }

    try {
      setSubmitting(true);
      const res = await customerPortalAPI.submitTestimonial(formData);
      
      if (res.data.success) {
        toast.success('Thank you! Your testimonial has been submitted for review.');
        setShowSubmitDialog(false);
        setFormData({
          title: '',
          content: '',
          rating: 5,
          company_name: '',
          job_title: '',
          use_case: '',
          allow_public: true,
          allow_photo: false
        });
        fetchTestimonials();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await customerPortalAPI.deleteTestimonial(testimonialId);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Only pending testimonials can be deleted');
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <button
            key={i}
            type="button"
            disabled={!interactive}
            onClick={() => onRate && onRate(i + 1)}
            className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          >
            <Star
              className={`h-6 w-6 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  const getStatusIcon = (status) => {
    const Icon = statusIcons[status] || Clock;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-portal-testimonials">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Testimonials</h1>
          <p className="text-muted-foreground">Share your experience with our products</p>
        </div>
        <Button onClick={() => setShowSubmitDialog(true)} data-testid="new-testimonial-btn">
          <Plus className="h-4 w-4 mr-2" />
          Write a Testimonial
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="p-3 bg-purple-100 rounded-lg h-fit">
              <Sparkles className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-900 mb-1">Your Voice Matters!</h3>
              <p className="text-sm text-purple-700">
                Share your experience to help other customers make informed decisions. 
                Approved testimonials may be featured on our website and marketing materials.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testimonials List */}
      <Card>
        <CardHeader>
          <CardTitle>Your Testimonials</CardTitle>
          <CardDescription>Track the status of your submissions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : testimonials.length > 0 ? (
            <div className="space-y-4">
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className="p-5 bg-slate-50 rounded-xl"
                  data-testid={`testimonial-item-${testimonial.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        {renderStars(testimonial.rating)}
                        <Badge className={`${statusColors[testimonial.status]} flex items-center gap-1`}>
                          {getStatusIcon(testimonial.status)}
                          {testimonial.status}
                        </Badge>
                        {testimonial.featured && (
                          <Badge className="bg-purple-100 text-purple-700 flex items-center gap-1">
                            <Award className="h-3 w-3" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-lg">{testimonial.title}</h3>
                    </div>
                    {testimonial.status === 'pending' && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(testimonial.id)}
                        data-testid={`delete-testimonial-${testimonial.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-slate-200 mb-3">
                    <Quote className="h-5 w-5 text-slate-300 mb-2" />
                    <p className="text-slate-700 italic">{testimonial.content}</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {testimonial.company_name && (
                      <span>Company: {testimonial.company_name}</span>
                    )}
                    {testimonial.job_title && (
                      <span>Role: {testimonial.job_title}</span>
                    )}
                    <span>Submitted: {format(new Date(testimonial.created_at), 'MMM d, yyyy')}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No testimonials yet</h3>
              <p className="text-muted-foreground mb-4">
                Share your experience with our products to help others
              </p>
              <Button onClick={() => setShowSubmitDialog(true)}>
                <Star className="h-4 w-4 mr-2" />
                Write Your First Testimonial
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Write a Testimonial</DialogTitle>
            <DialogDescription>
              Share your experience with our products and services
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Rating */}
            <div>
              <Label className="mb-2 block">Your Rating *</Label>
              <div className="flex items-center gap-3">
                {renderStars(formData.rating, true, (rating) => setFormData(prev => ({ ...prev, rating })))}
                <span className="text-sm text-muted-foreground">{formData.rating} out of 5</span>
              </div>
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Excellent product quality!"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="testimonial-title-input"
              />
            </div>

            {/* Content */}
            <div>
              <Label htmlFor="content">Your Testimonial *</Label>
              <Textarea
                id="content"
                placeholder="Share your experience in detail..."
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={4}
                data-testid="testimonial-content-input"
              />
            </div>

            {/* Company & Job Title */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company">Company (optional)</Label>
                <Input
                  id="company"
                  placeholder="Your company name"
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="job_title">Job Title (optional)</Label>
                <Input
                  id="job_title"
                  placeholder="Your role"
                  value={formData.job_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                />
              </div>
            </div>

            {/* Use Case */}
            <div>
              <Label htmlFor="use_case">How do you use our product? (optional)</Label>
              <Textarea
                id="use_case"
                placeholder="Describe your use case..."
                value={formData.use_case}
                onChange={(e) => setFormData(prev => ({ ...prev, use_case: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Permissions */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="allow_public"
                  checked={formData.allow_public}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_public: checked }))}
                />
                <div>
                  <Label htmlFor="allow_public" className="font-normal cursor-pointer">
                    Allow public display
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Your testimonial may be displayed on our website
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="allow_photo"
                  checked={formData.allow_photo}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_photo: checked }))}
                />
                <div>
                  <Label htmlFor="allow_photo" className="font-normal cursor-pointer">
                    Allow photo usage
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    We may use your profile photo alongside your testimonial
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !formData.title || !formData.content}
              data-testid="submit-testimonial-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Star className="h-4 w-4 mr-2" />
                  Submit Testimonial
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalTestimonialsPage;
